import { ScreenWidthType } from 'components/types'
import { StateType } from 'data/types'
import isNil from 'lodash/isNil'
import { LanguageType } from 'providers/ConnectedIntl/languages'
import { createSelector } from 'reselect'

import { AppStateType } from './reducers'
import {
  ErrorType,
  FormattedCountryType,
  ModalNameType,
  ModalOptionsType,
  NotificationType,
  VeriffMetadataType
} from './types'

const getApp = (state: StateType) => state.app

const toListItem = ({ code, name, scopes }) => ({
  available: scopes.includes('MERCURY'),
  text: name,
  value: code
})

const getCountries: (state: StateType) => Array<FormattedCountryType> = createSelector(
  getApp,
  (app: AppStateType) => {
    const countries = app.countries ?? []
    return countries.map(toListItem)
  }
)

const getExchangeAllowedCountries = (state: StateType) =>
  (state.app.countries ?? []).filter((c) => c.scopes.includes('MERCURY'))

const getLanguage: (state: StateType) => LanguageType = createSelector(
  getApp,
  (app) => app.language || 'en'
)

const getModalFullscreen: (state: StateType) => boolean = createSelector(
  getApp,
  (app) => app.modalFullscreen || false
)

const getModalLargescreen: (state: StateType) => boolean = createSelector(
  getApp,
  (app) => app.modalLargescreen || false
)

const getModalDismissable: (state: StateType) => boolean = createSelector(getApp, ({ modal }) =>
  isNil(modal) ? true : modal.isDismissable
)

const getModalOptions: (state: StateType) => ModalOptionsType = createSelector(
  getApp,
  ({ modal }) => modal.options
)

const getModalCurrency: (state: StateType) => string | undefined = createSelector(
  getApp,
  ({ modal }) => modal.options.currency
)

const getModalName: (state: StateType) => ModalNameType | undefined = createSelector(
  getApp,
  ({ modal }) => modal.name
)

const getNotifications: (state: StateType) => Array<NotificationType> = createSelector(
  getApp,
  ({ notifications }) => notifications
)

const getScreenWidth: (state: StateType) => undefined | ScreenWidthType = createSelector(
  getApp,
  ({ width }) => width
)

const getUsStates: (state: StateType) => Array<FormattedCountryType> = createSelector(
  getApp,
  (app: AppStateType) => {
    const states = app.usStates ?? []
    return states.map(toListItem)
  }
)

const getRawUsStates = (state: StateType) => state.app.usStates ?? []

const getVeriffMetadata: (state: StateType) => VeriffMetadataType | undefined = createSelector(
  getApp,
  ({ veriffMetadata }) => veriffMetadata
)

const getVeriffMetadataError: (state: StateType) => ErrorType | undefined = createSelector(
  getApp,
  ({ veriffMetadataError }) => veriffMetadataError
)

export default {
  getCountries,
  getExchangeAllowedCountries,
  getLanguage,
  getModalCurrency,
  getModalDismissable,
  getModalFullscreen,
  getModalLargescreen,
  getModalName,
  getModalOptions,
  getNotifications,
  getRawUsStates,
  getScreenWidth,
  getUsStates,
  getVeriffMetadata,
  getVeriffMetadataError
}
